import { bootstrapApplication } from '@angular/platform-browser';
import { ErrorHandler, provideZoneChangeDetection } from '@angular/core';
import { ChunkLoadErrorHandler } from './app/core/error-handlers/chunk-load-error.handler';
import { RouteReuseStrategy, provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAuth0 } from '@auth0/auth0-angular';
import { environment } from './environments/environment';
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import { provideToastr } from 'ngx-toastr';
import { provideMarkdown, MARKED_EXTENSIONS } from 'ngx-markdown';
import { marked } from 'marked';
import { addIcons } from 'ionicons';
import {
  menuOutline,
  closeOutline,
  addOutline,
  attachOutline,
  mic,
  micOutline,
  optionsOutline,
  checkmark,
  informationCircleOutline,
  close,
  arrowForward,
  stop,
  searchOutline,
  ellipsisHorizontalOutline,
  ellipsisVerticalOutline,
  trashOutline,
  receiptOutline,
  timeOutline,
  bookOutline,
  calendarOutline,
  documentTextOutline,
  sparkles,
  list,
  documentText,
  downloadOutline,
  cloudDownloadOutline,
  arrowBack,
  chevronUp,
  chevronDown,
  shareOutline,
  refreshOutline,
  chevronBackOutline,
  chevronForwardOutline,
  alertCircleOutline,
  cloudUploadOutline,
  addCircleOutline,
  arrowUpOutline,
  arrowDownOutline,
  helpCircleOutline,
  videocamOutline,
  businessOutline,
  pencilOutline,
  createOutline,
  eye,
  eyeOff,
  lockClosedOutline,
  arrowBackCircleOutline,
  openOutline,
  readerOutline,
  handRightOutline,
  personOutline,
  pencil,
  logOutOutline,
  documentOutline,
  gridOutline,
  imageOutline,
  syncOutline,
  checkmarkOutline,
  arrowRedo,
  cloudDownload,
  trash,
  // Firm workspace icons
  add,
  business,
  briefcase,
  briefcaseOutline,
  chevronForward,
  closeCircleOutline,
  gitNetwork,
  gitNetworkOutline,
  mailOutline,
  people,
  peopleOutline,
  personAdd,
  personAddOutline,
  personRemoveOutline,
  personCircleOutline,
  swapHorizontal,
  cardOutline,
  linkOutline,
  sync,
} from 'ionicons/icons';
import { AppComponent } from './app/app.component';
import { routes } from './app/app-routing.module';
import { authInterceptor } from './app/core/services/auth-interceptor';
import { apiInterceptor } from './app/core/services/api.interceptor';
import { firmContextInterceptor } from './app/firm-workspace/shared/interceptors/firm-context.interceptor';
import posthog from 'posthog-js';
import config from '../capacitor.config';
import { Capacitor } from '@capacitor/core';
import { stringConstant } from './app/core/services/stringConstants';

// Register the icons with both names to ensure compatibility
addIcons({
  'eye': eye,
  'eye-off': eyeOff,
  'arrow-back-circle-outline': arrowBackCircleOutline,
  'menu-outline': menuOutline,
  'close-outline': closeOutline,
  'add-outline': addOutline,
  'attach-outline': attachOutline,
  mic: mic,
  'mic-outline': micOutline,
  'options-outline': optionsOutline,
  checkmark: checkmark,
  'chevron-up': chevronUp,
  'chevron-down': chevronDown,
  'chevron-up-outline': chevronUp,
  'chevron-down-outline': chevronDown,
  'chevron-back-outline': chevronBackOutline,
  'chevron-forward-outline': chevronForwardOutline,
  'arrow-down-outline': arrowDownOutline,
  'videocam-outline': videocamOutline,
  'business-outline': businessOutline,
  'information-circle-outline': informationCircleOutline,
  'alert-circle-outline': alertCircleOutline,
  close: close,
  'arrow-forward': arrowForward,
  stop: stop,
  'arrow-up-outline': arrowUpOutline,
  'search-outline': searchOutline,
  'ellipsis-horizontal-outline': ellipsisHorizontalOutline,
  'ellipsis-vertical-outline': ellipsisVerticalOutline,
  'trash-outline': trashOutline,
  'trash': trash,
  'pencil-outline': pencilOutline,
  'create-outline': createOutline,
  'open-outline': openOutline,
  'receipt-outline': receiptOutline,
  'time-outline': timeOutline,
  'book-outline': bookOutline,
  'calendar-outline': calendarOutline,
  'document-text-outline': documentTextOutline,
  'share-outline': shareOutline,
  'refresh-outline': refreshOutline,
  'cloud-upload-outline': cloudUploadOutline,
  'add-circle-outline': addCircleOutline,
  'help-circle-outline': helpCircleOutline,
  sparkles: sparkles,
  list: list,
  'document-text': documentText,
  // Add alternative names for better compatibility
  'information-circle': informationCircleOutline,
  'close-circle': close,
  'arrow-forward-outline': arrowForward,
  search: searchOutline,
  'ellipsis-horizontal': ellipsisHorizontalOutline,
  'ellipsis-vertical': ellipsisVerticalOutline,
  receipt: receiptOutline,
  time: timeOutline,
  book: bookOutline,
  calendar: calendarOutline,
  'sparkles-outline': sparkles,
  'list-outline': list,
  'download-outline': downloadOutline,
  'cloud-download-outline': cloudDownloadOutline,
  'arrow-back': arrowBack,
  'arrow-back-outline': arrowBack,
  'arrow-back-sharp': arrowBack,
  back: arrowBack,
  'chevron-back': chevronBackOutline,
  'chevron-back-sharp': chevronBackOutline,
  'reader-outline': readerOutline,
  'hand-right-outline': handRightOutline,
  'person-outline': personOutline,
  'pencil': pencil,
  'log-out-outline': logOutOutline,
  'document-outline': documentOutline,
  'grid-outline': gridOutline,
  'image-outline': imageOutline,
  'sync-outline': syncOutline,
  'checkmark-outline': checkmarkOutline,
  // Firm workspace
  'add': add,
  'business': business,
  'briefcase': briefcase,
  'briefcase-outline': briefcaseOutline,
  'chevron-forward': chevronForward,
  'close-circle-outline': closeCircleOutline,
  'git-network': gitNetwork,
  'git-network-outline': gitNetworkOutline,
  'mail-outline': mailOutline,
  'people': people,
  'people-outline': peopleOutline,
  'person-add': personAdd,
  'person-add-outline': personAddOutline,
  'person-remove-outline': personRemoveOutline,
  'arrow-redo': arrowRedo,
  'cloud-download': cloudDownload,
  'link-outline': linkOutline,
  'sync': sync,
  'person-circle-outline': personCircleOutline,
  'swap-horizontal': swapHorizontal,
  'card-outline': cardOutline,
  'lock-closed-outline': lockClosedOutline,
});

// Global marked renderer: all links open in a new tab and internal root-relative
// URLs are made absolute so browsers never misinterpret the path as a hostname.
marked.use({
  renderer: {
    link(token: { href?: string | null; title?: string | null; text: string }): string {
      let href = token.href || '';
      if (href.startsWith('/')) {
        href = window.location.origin + href;
      }
      const title = token.title ? ` title="${token.title}"` : '';
      return `<a href="${href}"${title} target="_blank" rel="noopener noreferrer">${token.text}</a>`;
    },
  },
});

// Re-export the same extension to ngx-markdown so the <markdown> directive
// picks up the same renderer even if it initialises its own marked instance.
const _answerLinksExtension = {
  renderer: {
    link(token: { href?: string | null; title?: string | null; text: string }): string {
      let href = token.href || '';
      if (href.startsWith('/')) {
        href = window.location.origin + href;
      }
      const title = token.title ? ` title="${token.title}"` : '';
      return `<a href="${href}"${title} target="_blank" rel="noopener noreferrer">${token.text}</a>`;
    },
  },
};

// Initialize PostHog before bootstrapping the app
if (environment.posthog?.Key && environment.posthog?.Host) {
  try {
    posthog.init(environment.posthog.Key, {
      api_host: environment.posthog.Host,
      loaded: (posthog) => {
        // Feature flags are loaded automatically after initialization
      },
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: false, // Optional: disable autocapture if not needed
    });
  } catch (error) {
    // log error
  }
} else {
  // log error
}

// Build the redirect_uri based on platform
// For Android/iOS: use custom URL scheme
// For Web: use window.location.origin
const isNative = Capacitor.isNativePlatform();
const redirect_uri = isNative
  ? `${config.appId}://${environment.auth.domain}${stringConstant.capacitorCallbackPath}/${config.appId}${stringConstant.slashCallback}`
  : environment.auth.authorizationParams.redirect_uri;

bootstrapApplication(AppComponent, {
  providers: [
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular(),
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withInterceptors([apiInterceptor, authInterceptor, firmContextInterceptor])),
    provideAuth0({
      ...environment.auth,
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true, // Fall back to iframe silent auth when refresh token is missing
      authorizationParams: {
        ...environment.auth.authorizationParams,
        redirect_uri,
      },
    }),
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
    }),
    provideMarkdown({
      markedExtensions: [
        { provide: MARKED_EXTENSIONS, useValue: _answerLinksExtension, multi: true },
      ],
    }),
  ],
});


